// import { ParamateConfigurator } from '../../ext/paramate-frontend/src/paramateConfigurator';
import './overlays/as-modal-overlay.ts'
import './error/as-error-info.ts'
import './overlays/as-notifications.ts'
import './overlays/as-modal-home-warning.ts'
import { commonAppData, IconListing } from '../store/store.ts';
import { storeMesh, type StoredMeshData, checkForStoredMeshData, TransferSaveData, setNewApp, deleteAllStoredTransfers, deleteMesh, getSenderApp } from '../logic/db.ts';
import { AsModalOverlay } from './overlays/as-modal-overlay.ts';
import sharedLocalisationDict from '../data/sharedLocalisation.json'
import { getUserData, DefaultLogEventStrings, sendLogToServer, UserData } from '../logic/asApi.ts';
import { fullAppSuiteRootURL, isProduction, localMode } from '../data/constants.ts';
import packageJson from '../../package.json';
import { detectDeviceInfo, devlog, dispatchCustomEv, UAContainsMobileKeywords } from '../logic/utils.ts';
import styleText from './as-app.css?raw';

devlog("[CONSTANTS] Localmode",localMode,"Production?",isProduction)

import { defaultAppInfo } from '../data/defaultAppInfo.cfg.ts';
import { AsConfigurator } from './paramate/as-configurator.ts';

// devlog(defaultAppInfoCfg)
// // Module-level promise to fetch app info once when module loads
// const appInfoPromise = new Promise((resolve,reject)=>{
//   // if(localMode)resolve(defaultAppInfoCfg)
  
//   import(/* @vite-ignore */
//     (isProduction)
//     /* @vite-ignore */
//     ?"https://apps.trinckle.com/data/appInfo.cfg.js"
//     :`${fullAppSuiteRootURL}/data/appInfo.cfg.js` as any
//     )
//     .then((module: any) => {

//       resolve(module.default || module) // Handle both default export and named exports
//     })
//     .catch(error => {
//       console.error('Failed to load app info:', error);
//       return {}; // Return empty object as fallback
//     });

// })

let appInfoCached: boolean = false;
// Simple object that gets populated when data loads
let appInfo: any = {};

// Helper function to get app info (async)
const getAppInfo = async (): Promise<any> => {
  if (!appInfoCached) {
    try {
      if(localMode){
        appInfo = defaultAppInfo;
      }else{
        const response = await fetch(`${fullAppSuiteRootURL}/data/appInfo.cfg.json`);
        if (!response.ok) {
          throw new Error(`Failed to fetch appInfo: ${response.status} ${response.statusText}`);
        }
        appInfo = await response.json();
      }
      appInfoCached = true;
      window.dispatchEvent(new CustomEvent('appinfo-ready', { detail: appInfo }));
    } catch (error) {
      console.error('[AS-APP] Failed to load appInfo.cfg.json:', error);
      // Ensure appInfoCached stays false so we can retry
      appInfoCached = false;
      throw error;
    }
  }
  if(!isProduction)devlog("appInfo",appInfo)
  return appInfo;
};

devlog(`${fullAppSuiteRootURL}/data/appInfo.cfg.json`)


/**
 * Global interface declarations for external libraries
 */
declare global { // TODO: fix paramate-frontend libs
  interface Window {
    ParamateConfigurator: any;
    ParamateViewportWebGL: any;
  }
}

/**
 * Custom event interface for mesh retrieval operations
 */
export interface GetMeshEvent extends CustomEvent<{
  resolve: Function, // Respond to the request with a blob
  reject: Function, // Respond to the request with an error
  finalized?: boolean, // Whether to get finalized model URL
  filename?: string, // Filename for the model
  type?: string // Model type (e.g., 'stl')
}> {}

/**
 * Interface for modal content management
 * Defines the structure of different modal types and their associated data
 */
interface ModalContent {
  type: 'saving' | 'success' | 'error' | 'none' | 'history' | 'autoload';
  data?: {
    blob?: Blob;
    error?: string;
    availableApps?: string[];
    meshes?: StoredMeshData[];
    autoLoadMesh?: StoredMeshData;
    status?: string;
    pendingStoreData?: TransferSaveData;
  };
}

// Define language abbreviation map
export const langAbbrev = {
  "en": "English",
  "de": "Deutsch",
  "es": "Español",
  "fr": "Français",
  "jp": "日本語",
  "cn": "中文",
  "it": "Italiano",
  "ko": "한국어",
  "pt": "Português",
  "ukr": "Українська",
  "dk": "Dansk",
  "nl": "Nederlands"
} as const;

// After the langAbbrev definition add:
// Default translations for common error messages and UI elements
const defaultTranslations = {
  "en": {
    "Base.ErrorHeader": "Error",
    "Base.ErrorInfo.ErrorDescription.Generic": "An unexpected error occurred",
    "errorPanel.label.timestamp": "Timestamp",
    "errorPanel.label.sessionId": "Session ID",
    "errorPanel.label.error": "Error",
    "errorPanel.label.serverCode": "Server Code",
    "Base.ErrorPanel.Button.CopyToClipboard": "Copy to Clipboard",
    "Base.ErrorPanel.Button.Undo": "Undo",
    "Base.AppTitle": "App Suite"
  }
};

const handlers = {
    
}

/**
 * Get filtered list of apps based on criteria
 * @param filter - Filter criteria: 'all', 'free', 'paid'
 * @returns Promise<string[]> - Array of app keys
 */
const getFilteredApps = async (
  filter: 'all' | 'free' | 'paid' = 'all', 
  onlyTransferable: boolean = true,
  exclude: Array<string> = []
): Promise<string[]> => {
  const appInfoData = await getAppInfo();
  
  return Object.entries(appInfoData).filter(([key, value]) => {
    const appConfig = value as any;
    
    // Filter out hidden apps
    if (isProduction && appConfig?.visibility?.production===false) {
      return false;
    }
    if (!localMode && appConfig?.visibility?.staging===false) {
      return false;
    }

    if(onlyTransferable && !appConfig?.acceptsFileTransfer){
      return false;
    }
    if(exclude.includes(key)){
      return false;
    }
    
    // Apply license type filter
    switch (filter) {
      case 'free':
        return appConfig?.free === true;
      case 'paid':
        return appConfig?.free !== true; // undefined or false means paid
      case 'all':
      default:
        return true;
    }
  }).map(([key]) => key);
}

// Synchronous version using cached appInfo data
const getFilteredAppsSync = (
  filter: 'all' | 'free' | 'paid' = 'all',
  onlyTransferable: boolean = true,
  exclude: Array<string> = []
): string[] => {
  // Use cached appInfo data directly
  if (!appInfoCached) {
    console.error('[AS-APP] getFilteredAppsSync called before appInfo was loaded. This will result in an empty app list.');
    return []; // Return empty array if data not loaded yet
  }
  if(!isProduction)devlog(appInfo)
  return Object.entries(appInfo).filter(([key, value]) => {
    const appConfig = value as any;
    
   // Filter out hidden apps
    if (isProduction && appConfig?.visibility?.production===false) {
      return false;
    }
    if (!localMode && appConfig?.visibility?.staging===false) {
      return false;
    }
    if(onlyTransferable && !appConfig?.acceptsFileTransfer){
      return false;
    }
    if(exclude.includes(key)){
      return false;
    }
    
    // Apply license type filter
    switch (filter) {
      case 'free':
        return appConfig?.free === true;
      case 'paid':
        return appConfig?.free !== true; // undefined or false means paid
      case 'all':
      default:
        return true;
    }
  }).map(([key]) => key);
}

// Convenience methods for backward compatibility
const getAvailableAppKeys = (): Promise<string[]> => getFilteredApps('all',true,[]);
const getFreeApps = (): Promise<string[]> => getFilteredApps('free');
const getPaidApps = (): Promise<string[]> => getFilteredApps('paid');

const checkLicenseType=()=>{
  //
}
/**
 * AsApp custom element
 * 
 * Main application component that manages:
 * - App configuration and initialization
 * - Mesh data storage and retrieval
 * - Modal dialogs for mesh operations (saving, transferring, history)
 * - Error handling
 * - Language and localization
 * - Inter-app communication
 */
export class AsApp extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  // Map to track text subscribers - each key can have multiple elements
  private textSubscribers: Map<string, Set<HTMLElement>> = new Map();
  private langText:{[lang: string]: {[key: string]: string}}|null = null;
  private sharedLocalisation:{[lang: string]: {[key: string]: string}} = sharedLocalisationDict;
  private isInitialized:boolean = false;
  private postInitQueue:Array<()=>void> = [];
  private language:string = 'en'; // Default to English
  private userData:UserData|null = null;
  private version:string = packageJson.version;
  private trcadVersions:{trCad:string,script:string}|undefined
  
  get appInfo(){return appInfo}
  /**
   * Mesh data storage
   * Contains references to latest mesh, autoload mesh, and mesh history
   */
  private meshData: { 
    latestMesh: StoredMeshData | null; 
    autoLoadMesh: StoredMeshData | null;
    totalCount: number;
    meshes?: StoredMeshData[];
  };

  private appIsPaid=()=>{
    // devlog(appInfo);
    const isFree = appInfo?.[this.appId]?.free;
    if(isFree==undefined) console.warn(`App ${this.appId} has no license type defined.`);
    return !isFree;
  };

  public canDownload=()=>{
    const isPaidType = this.appIsPaid();
    if(isPaidType===undefined) {
      this.dispatchEvent(new CustomEvent('error', {detail: {error: `App ${this.appId} has no license type defined. Check your app-id and cfg file!`}}));
      return false;
    }
    if(isPaidType===false) return true;
    if(isPaidType) return this.userHasLicense();
    return false;
  }

  private userHasLicense=()=>{
    return this.userData?.license.type !== null;
  }
  
  /** Current application ID */
  private appId: string;

  public mobileDetected:boolean|undefined=undefined;

  /** List of available apps for mesh transfer */
  private availableApps: string[] = [];
  
  /** Modal state management */
  private modalContent: ModalContent = { type: 'none' };
  private modalOverlay: AsModalOverlay | null = null;
  private openTransferInNewTab: boolean = false;
  private unsavedChanges = false;
  private isHomeRedirectPending = false;
  /**
   * Get list of attributes to observe for changes
   */
  static get observedAttributes(): string[] {
    return ['onprem', 'lang', 'app-id', 'server-root', 'assets-path'];
  }
    /**
   * Initialize the AsApp component
   * 
   * Sets up:
   * - Shadow DOM
   * - App configuration
   * - Event listeners for language, errors, and mesh operations
   * - Modal container for dialogs
   * - Error handling infrastructure
   */
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.shadowRoot!.adoptedStyleSheets = [
        ...this.shadowRoot!.adoptedStyleSheets,
        AsApp.sheet
      ];
      this.appId = this.getAttribute('app-id') ?? 'invalid';


      
      // Set initial language from attribute if provided
      this.language = this.getAttribute('lang') ?? 'en';
      
      this.meshData = { latestMesh: null, autoLoadMesh: null, totalCount: 0 };
      
      
      
      // Initialize server root and assets path from attributes if provided
      const serverRoot = this.getAttribute('server-root');
      const assetsPath = this.getAttribute('assets-path');
      


      if (serverRoot) commonAppData.serverRoot = serverRoot;
      if (assetsPath) commonAppData.assetsPath = assetsPath;
      
      // Initialize with the imported localisation dictionary
      // No need to fetch it again

      // Initialize available apps asynchronously
      this.initializeAvailableApps();

      // Load language file if specified
      this.fetchLangFiles();
  
      // ICONS

      this.fetchIconCfg();

  
      // Create main container with slot
      (this.shadowRoot) &&(
        this.shadowRoot.innerHTML = /*html*/`
          <div id="as-app-container">
            <slot></slot>
          </div>
          <as-notifications default-timeout="5000"></as-notifications>
          <as-modal-overlay id="as-app-overlay" mode="auto" visible-child="home-warning">
            <as-error-info id="main-error-panel"></as-error-info>
            <as-mobile-warning id="mobile-warning"></as-mobile-warning>
            <as-modal-home-warning id="home-warning"></as-modal-home-warning>
            <as-modal-clickthru-demo id="clickthru-modal" url=${this.getAttribute("clickthru-url")??''}></as-modal-clickthru-demo>
          </as-modal-overlay>
        `
      );

      // Set up event delegation for modal clicks in the constructor
      const modalOverlay = this.shadowRoot?.getElementById('as-app-overlay');
      if (modalOverlay) {
        modalOverlay.addEventListener('click', this.handleTxModalClick);
      }
  
      // Create configurator instance but don't initialize yet
      // this.createConfigurator();
    }
  
  /**
   * Get list of available apps excluding the current one
   */
  get otherAppsForTransfer(): string[] {
    return getFilteredAppsSync(
      'all',
      true,
      [this.appId]
    );
  }
  getVersion(){
    return this.version
  }
  async getTrCADVersions(){
    const output = {
      trCad:undefined,script:undefined
    }
    if(!this.trcadVersions){
      const configuratorEl = document.querySelector("as-configurator") as AsConfigurator;
      if(!configuratorEl){
        console.warn("[VERSIONING] as-configurator element not found");
        return undefined;
      }

      try {
        const params = await configuratorEl.getParams();
        const trCadScriptVer = params?.["main:metadata:scriptVersion"]?.default;
        const trCadVer= params?.["main:metadata:trcadVersion"]?.default;

        if(!trCadVer) devlog("[VERSIONING] trcadVersion not found in params main:metadata");
        if(!trCadScriptVer) devlog("[VERSIONING] scriptVersion not found in params main:metadata");
        
        output.trCad = trCadVer
        output.script = trCadScriptVer
        devlog("[VERSIONING]",output)
        this.trcadVersions = output;
        return output
      } catch(err) {
        console.error("[VERSIONING] Error getting TrCAD version:", err);
        return output
      };
    }else {
      return output
    }
  }

  async getVersions(){
    const {trCad,script} = await this.getTrCADVersions()
    return {
      components:this.version,
      app:this.getAttribute("app-version"),
      trCad,
      trCadScript:script
    }
  }
  /**
   * Initialize available apps asynchronously
   */
  private async initializeAvailableApps(): Promise<void> {
    try {
      this.availableApps = await getAvailableAppKeys();
    } catch (error) {
      console.error('Failed to initialize available apps:', error);
      this.availableApps = [];
    }
  }

  private fetchLangFiles(){
    const languageFileSrc = this.getAttribute('langFile') ?? '';
      if (languageFileSrc) {
        fetch(languageFileSrc)
          .then(response => response.json())
          .then(data => {
            this.langText = data;
            this.updateLangSubscribers();
          })
          .catch(err => {
            console.error('Error loading language file:', err);
          });
      };
  }
  
  private fetchIconCfg(){
    const root = "https://apps.trinckle.com"
    // First get the app path from the current URL or app ID
    const appPath = this.appId ? `apps/${this.appId}` : '';
    const customIconVariants = this.hasAttribute('custom-icon-variants') ?? false;
    // Create an array of paths to check
    const iconPaths = [
      `${root}/data/base-icon-variants.cfg.js`
    ];
    customIconVariants && iconPaths.push(`${root}/${appPath}/data/icon-variants.cfg.js`);
    
    // Create an array of promises for fetching from each path
    const fetchPromises = iconPaths.map(path => 
      fetch(path)
        .then(response => response.ok ? response.json() : {})
        .catch(() => ({})) // Return empty object on failure
    );

    // Use Promise.all to load from all paths
    Promise.all(fetchPromises)
      .then(results => {
        // Merge all results, with later results taking precedence
        const mergedIconVariants = results.reduce((acc, variants) => ({
          ...acc,
          ...variants
        }), {});
        
        // Update the icon list
        commonAppData._iconList = {
          ...commonAppData._iconList,
          ...mergedIconVariants
        };
      })
      .catch(err => {
        console.error('Error loading icon variants:', err);
      });
  }

  private updateLang(lang:string) {
    this.language = lang;

    
    // Update subscribers with new language immediately
    this.updateLangSubscribers();
    
    // Notify all as-lang-selector components to sync their visual state
    this.updateLangSelectors();
    
    // Dispatch language-changed event for any listening components
    this.dispatchEvent(new CustomEvent('as-language-changed', {
      bubbles: true,
      composed: true,
      detail: { lang }
    }));
    
    // Load language data if not already loaded and langFile attribute is present
    if (!this.langText) {
      const languageFileSrc = this.getAttribute('langFile') ?? '';
      if (languageFileSrc) {
        fetch(languageFileSrc)
          .then(response => response.json())
          .then(data => {
            this.langText = data;
            this.updateLangSubscribers();
          })
          .catch(err => {
            console.error('Error loading language file:', err);
          });
      }
    }
  }
  /**
   * Handle attribute changes
   * 
   * @param name - Name of the changed attribute
   * @param oldValue - Previous attribute value
   * @param newValue - New attribute value
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'lang':
        this.updateLang(newValue);
        break;
      case 'app-id':
        // devlog("App ID changed", newValue);
        this.appId = newValue;
        break;
      case 'server-root':
        if (newValue) {
          commonAppData.serverRoot = newValue;
        }
        break;
      case 'assets-path':
        if (newValue) {
          commonAppData.assetsPath = newValue;
        }
        break;
      // Add other attribute handlers as needed
    }
  }

  /**
   * Update all language text subscribers with current language text
   * Notifies all registered elements with the localized text for their keys
   * Checks sharedLocalisation first, then falls back to langText
   */
  updateLangSubscribers() {
    // Update all subscribers with their localized text
    this.textSubscribers.forEach((subscribers, textKey) => {
      subscribers.forEach(subscriber => {
        this.sendTextToSubscriber(textKey, subscriber);
      });
    });
  }

  /**
   * Update all as-lang-selector components within this as-app to sync their visual state
   */
  private updateLangSelectors() {
    // Find all as-lang-selector elements within this as-app
    const langSelectors = this.querySelectorAll('as-lang-selector');
    langSelectors.forEach((selector: any) => {
      if (selector.setLanguage && typeof selector.setLanguage === 'function') {
        selector.setLanguage(this.language);
      }
      // Also update the attribute for consistency
      selector.setAttribute('language', this.language);
    });
  }


  public initComponentStore(opts:{
    serverRoot?:string,
    assetsPath?:string,
    appLangFileName?:string,
    additionalIconPaths?:IconListing
  }){
    Object.entries(opts).forEach(([key,value]) => {
      switch(key){
        case 'serverRoot':
          commonAppData.serverRoot = value;
          break;
        case 'assetsPath':
          commonAppData.assetsPath = value;
          break;
        case 'appLangFileName':
          commonAppData.appLangFileName = value;
          break;
        case 'lang':
          this.updateLang(value as string);
          break;
        case 'additionalIconPaths':
          commonAppData._iconList = {
            ...commonAppData._iconList,
            ...value as IconListing
          };
          break;
      }
    });
  }

  /**
   * Setup component when connected to DOM
   * 
   * Performs:
   * - Mesh data initialization and auto-loading
   * - Event listener setup for mesh operations
   * - Error handler registration
   * - Component availability broadcasting
   */
  async connectedCallback() {
    this.modalOverlay  = this.shadowRoot?.getElementById('as-app-overlay') as AsModalOverlay;

    getUserData().then(res=>{
      this.userData = res.userData;

      //@ts-ignore
      const stopRedir = window?.stopRedir
      if(stopRedir) {
        devlog("User data response:",res.userData)
        devlog("local?",localMode,"dev?",this.hasAttribute("dev-mode"),"authed?",res.isAuthenticated)
      } 
      else if( !(stopRedir || localMode || this.hasAttribute('dev-mode')) &&!res.isAuthenticated){
        //redirect
        window.location.href = `${fullAppSuiteRootURL}/?modal=sign-in-form&redirApp=${this.appId}`;
      }
    });

    if (this.appId === 'invalid' || this.appId === 'template') {
      alert('app-id is required attribute on as-app');
    } else{
      commonAppData.appId = this.appId;
    }


    //after saving commonAppData
    sendLogToServer('app_opened')
    .catch(console.error);


    // devlog("=== AS APP: connectedCallback");
      
    // Listen for text subscription requests
    this.addEventListener('as-local-text-subscribe', (event: CustomEvent) => {
      const { textKey, requester } = event.detail;
      
      // Get or create the set for this text key
      if (!this.textSubscribers.has(textKey)) {
        this.textSubscribers.set(textKey, new Set());
      }
      this.textSubscribers.get(textKey)!.add(requester);

      // Find and send text for the current language
      this.sendTextToSubscriber(textKey, requester);
    });

    // Listen for text unsubscription requests
    this.addEventListener('as-local-text-unsubscribe', (event: CustomEvent) => {
      const { textKey, requester } = event.detail;
      
      // Remove the requester from the set for this key
      const subscribers = this.textSubscribers.get(textKey);
      if (subscribers) {
        subscribers.delete(requester);
        // If no more subscribers for this key, remove the entry
        if (subscribers.size === 0) {
          this.textSubscribers.delete(textKey);
        }
      }
    });
  

    // Load mesh data with autoLoad check
    this.meshData = await checkForStoredMeshData(this.appId, true, true);
    
    // If we have an autoLoad mesh, show the autoLoad dialog
    if (this.meshData.autoLoadMesh) {
      this.showAutoLoadDialog(this.meshData.autoLoadMesh);
    }
    // No longer creating default mesh data if none found

    // Listen for configurator initialized event
    window.addEventListener('configurator-initialized', this.handleConfiguratorInitialized);
    window.addEventListener('as-download-mesh', (event: CustomEvent) => {
      const { finalized = false, type = 'stl', fileHandle } = event.detail || {};
      this.downloadMesh(finalized, type, fileHandle);
    });
    // Listen for show-stored-meshes event
    this.addEventListener('show-stored-meshes', this.handleShowStoredMeshes);
    
    // Listen for mesh-processed event to mark a mesh as processed and possibly delete it
    this.addEventListener('mesh-processed', this.postMeshReceiveCleanup);
    this.addEventListener('as-mesh-rx-completed', this.postMeshReceiveCleanup);
    
    this.addEventListener('as-mesh-import-done', (ev:CustomEvent)=>{ //TODO: docs
      this.sendLog('import_mesh_done',{filetype:ev.detail?.filetype});
    });

    this.addEventListener('as-mesh-export-done', (ev:CustomEvent)=>{
      this.sendLog('export_mesh_done',{filetype:ev.detail?.filetype});
    }); 

    

    this.addEventListener("as-get-app", (event: CustomEvent) => {
      event.detail.requester.dispatchEvent(new CustomEvent('as-app', {
        detail: {sender:this}
      }));
    });

    // Listen for language query events
    this.addEventListener('as-get-current-language', (event: CustomEvent) => {
      if (event.detail && event.detail.requester) {
        event.detail.requester.dispatchEvent(new CustomEvent('as-current-language', {
          detail: { language: this.language }
        }));
      }
    });

    // Listen for initial language requests from as-lang-selector
    this.addEventListener('as-get-init-lang', (event: CustomEvent) => {
      if (event.detail && event.detail.requester) {
        // Send the current language to the requesting as-lang-selector
        if (event.detail.requester.setLanguage && typeof event.detail.requester.setLanguage === 'function') {
          event.detail.requester.setLanguage(this.language);
        }
      }
    });



    // Application-specific error handler for custom error events
    // This handles errors that are explicitly dispatched by components
    // using dispatchEvent(new CustomEvent('error', ...))
    // These are typically application-specific errors that we want to handle gracefully
    this.addEventListener('error', (event: CustomEvent) => {
      console.error("Got error", event.detail.error);

      const errorPanel = this.shadowRoot?.getElementById('main-error-panel');
      errorPanel?.dispatchEvent(new CustomEvent('new-error', {
        detail: event.detail
      }));
      const errorPanelOverlay = this.shadowRoot?.getElementById('as-app-overlay');

      errorPanelOverlay?.dispatchEvent(new CustomEvent('open-modal'));
    });

    
    this.addEventListener('as-auth-sign-in-submit', (event: CustomEvent) => {
      // Handle sign-in submit
    });

    this.addEventListener('as-auth-sign-out-submit', (event: CustomEvent) => {
      // devlog("AsApp: Got as-signout-submit", event.detail);
    });

    this.addEventListener('get-available-downloads', (event: CustomEvent) => {
      // devlog("Getting available downloads");
      const response = {
        app:this.appId,
        downloads: 10
      }
      // devlog("Response", response);
      event.detail.resolve(response);
      // this.dispatchEvent(new CustomEvent('available-downloads', {
      //   detail: response
      // }));
    });
    
    
    // this.addEventListener('input',(event: InputEvent)=>{
      //   devlog("Got input", event.target);
    //   const target = event.target as HTMLElement;
    //   const handlers = {
      //     'transfer-input-open-in-new-tab': (input: HTMLInputElement)=>{
    //       const openInNewTab = input.checked;
    //       devlog("Transfer input open in new tab", openInNewTab);
    //     }
    //   }
    //   const ids = Object.keys(handlers);
    //   ids.forEach(id=>{
    //     const input = target.querySelector(`#${id}`) as HTMLInputElement;
    //     if(input){
      //       handlers[id](input);
      //     }
      //   });
      
      // });
      
    this.addEventListener('as-transfer-mesh',this.prepareTransferMesh);
 
    this.addEventListener('as-button-click', async (event: CustomEvent) => {
      // devlog("Got as-button-clicked", event.detail);
      const {buttonName} = event.detail
      switch (buttonName) {
        case 'transfer-mesh':
          this.prepareTransferMesh(event);
          break;
        case 'home':
          if (this.unsavedChanges) {
            this.isHomeRedirectPending = true;
            this.openHomeWarningModal();
          } else {
            this.redirectToStorefront();
          }
          break;
        case 'cancel-exit-app':
          if (this.isHomeRedirectPending) {
            this.closeHomeWarningModal();
          }
          break;
        case 'confirm-exit-app':
          if (this.isHomeRedirectPending) {
            this.closeHomeWarningModal();
            this.redirectToStorefront();
          }
          break;
        case 'header-help-button':
          const helpUrl = this.getAttribute('clickthru-url');
          const clickthruMode = this.getAttribute('clickthru-mode') || 'external'; // 'external' or 'iframe'

          if (!helpUrl) {
            console.warn('No clickthru-url attribute set on as-app');
            break;
          }

          if (clickthruMode === 'iframe') {
            // Open in modal with iframe
            this.openClickthruModal();
          } else {
            // Open in new tab (default)
            window.open(helpUrl, '_blank');
          }
          break;
        case 'close-clickthru-modal':
          this.closeClickthruModal();
          break;
        case 'open-clickthru-new-tab':
          const clickthruUrl = this.getAttribute('clickthru-url');
          if (clickthruUrl) {
            window.open(clickthruUrl, '_blank');
            this.closeClickthruModal();
          }
          break;
        default:
          break;
      }
      const buttonToLogEvName = {
        'new': 'new_session',
        'restore': 'restore_session',
        'save': 'save_session',
        'import-mesh': 'import_mesh_started',
        'export-mesh': 'export_mesh_started',
        // 'transfer-mesh': 'transfer_mesh_started',
      }
      const evName = buttonToLogEvName[buttonName];
      // devlog('as-button-click. Log?',evName)

      if(evName){
        sendLogToServer(evName).catch(console.error);
      }
      


    });

    
    // Listen for language change events
    this.addEventListener('as-change-language', (event: CustomEvent) => {
      this.updateLang(event.detail.lang);
    });

    this.addEventListener('as-input-change', () => {
      this.unsavedChanges = true;
    });
    window.addEventListener('as-project-change', () => {
      this.unsavedChanges = true;
    });

    this.addEventListener('as-project-saved', () => {
      this.unsavedChanges = false;
    });

    // Add get-configurator event listener
    // this.addEventListener('get-configurator', this.handleGetConfigurator );
    
    // Add param-change event listener
    this.addEventListener('param-change', this.handleParamChange.bind(this));


    this.addEventListener('get-lang', (event: CustomEvent) => {
      
      // const lang = appStore.$language.get();
      // devlog("Getting lang", lang);
  
      // event.detail.requester.dispatchEvent(new CustomEvent('sent-lang', {
      //   detail: lang,
      //   bubbles: true,
      //   composed: true  
      // }));
    });

    // Global error handler for uncaught JavaScript errors
    // This catches runtime errors, syntax errors, and other unhandled exceptions
    // that occur anywhere in the application
    window.addEventListener('error', (event: ErrorEvent) => {
      // Ignore ResizeObserver loop warnings - these are not actual errors
      if (event.message && event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        return;
      }
      
      devlog("Got error", event);
      const errorPanel = this.shadowRoot?.getElementById('main-error-panel');
      errorPanel?.dispatchEvent(new CustomEvent('new-error', {
        detail: {
          error: event.error,
          origin:'uncaught'  // Indicates this was an uncaught JavaScript error
        }
      }));
      const errorPanelOverlay = this.shadowRoot?.getElementById('as-app-overlay');
      errorPanelOverlay?.dispatchEvent(new CustomEvent('open-modal'));
    });

    

    window.dispatchEvent(new CustomEvent('as-app', {
      detail:{sender:this}
    }));

    const deviceInfo = detectDeviceInfo()
    if(deviceInfo.hasTouchScreen){
      
      if(this.hasAttribute("mobile-view")){
        console.warn("Custom mobile view is not implemented yet.")
      }
      devlog("[MOBILE]",UAContainsMobileKeywords())
      if(UAContainsMobileKeywords())this.mobileDetected = true
      if(UAContainsMobileKeywords() && this.appId!=="storefront"){
        dispatchCustomEv(this.modalOverlay,'as-set-visible-child',{id:"mobile-warning"})
        dispatchCustomEv(this.modalOverlay,'as-open-modal',)

      }

    }
  }

  /**
   * Cleanup when component is disconnected from DOM
   */
  disconnectedCallback(): void {
    // Remove event listeners to prevent memory leaks
    const modalOverlay = this.shadowRoot?.getElementById('as-app-overlay');
    if (modalOverlay) {
      modalOverlay.removeEventListener('click', this.handleTxModalClick);
    }
    
    window.removeEventListener('configurator-initialized', this.handleConfiguratorInitialized);
  }

  // // Add this method to TrApp class
  // private handleGetConfigurator = (event: CustomEvent<{resolve:Function,reject:Function}>): void => {
  //   event.preventDefault();
  //   const callback = event.detail?.callback;
  //   if (callback && typeof callback === 'function') {
  //     callback(appStore.configurator);
  //   }
  // }
  sendLog(evName:string,data?:any){
   return sendLogToServer(evName,data)
   .catch(console.error);
  }

  downloadMesh = async (finalized: boolean = false, type: string = 'stl', fileHandle?: any): Promise<void> => {
    try {
      if (fileHandle) {
        // Use provided fileHandle (File System Access API)
        const blob = await this.getMeshFromConfigurator(finalized, type, fileHandle.name);

        // Write to the selected file
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();

        devlog('Mesh saved successfully to:', fileHandle.name);

        // Dispatch export done event
        this.dispatchEvent(new CustomEvent('as-mesh-export-done', {
          bubbles:true,
          detail: { filetype: type }
        }));

      } else if ('showSaveFilePicker' in window) {
        // No fileHandle provided, but File System Access API available - show picker
        const selectedFileHandle = await (window as any).showSaveFilePicker({
          suggestedName: `mesh.${type}`,
          types: [{
            description: `${type.toUpperCase()} files`,
            accept: { [`application/${type}`]: [`.${type}`] }
          }]
        });

        // Get mesh data from configurator after file picker is shown
        const blob = await this.getMeshFromConfigurator(finalized, type, selectedFileHandle.name);

        // Write to the selected file
        const writable = await selectedFileHandle.createWritable();
        await writable.write(blob);
        await writable.close();

        devlog('Mesh saved successfully to:', selectedFileHandle.name);

        // Dispatch export done event
        this.dispatchEvent(new CustomEvent('as-mesh-export-done', {
          bubbles:true,
          detail: { filetype: type }
        }));

      } else {
        // Fallback: Use blob download method
        // Get mesh data from configurator first
        const blob = await this.getMeshFromConfigurator(finalized, type, `mesh.${type}`);

        // Create download link
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mesh.${type}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        devlog('Mesh downloaded as:', a.download);

        // Dispatch export done event
        this.dispatchEvent(new CustomEvent('as-mesh-export-done', {
          detail: { filetype: type }
        }));
      }
      
    } catch (error) {
      console.error('Error downloading mesh:', error);
      
      // Show error notification
      this.dispatchEvent(new CustomEvent('as-notify', {
        detail: { 
          defaultText: 'Error downloading mesh: ' + error.message, 
          dictKey: 'notifications.mesh.download.error',
          type: 'error'
        }
      }));
      
      // Also show in error panel
      const errorPanel = this.shadowRoot?.getElementById('main-error-panel');
      errorPanel?.dispatchEvent(new CustomEvent('new-error', {
        detail: {
          error: error,
          info: "Failed to download mesh",
          origin: 'mesh-download'
        }
      }));
      const errorPanelOverlay = this.shadowRoot?.getElementById('as-app-overlay');
      errorPanelOverlay?.dispatchEvent(new CustomEvent('open-modal'));
    }
  }
  
  // meshURL
  
  /**
   * Request mesh data from the configurator without showing modal
   * 
   * Creates a promise-based interface to the configurator's mesh export functionality
   * Implements timeout and error handling for robustness
   * 
   * @param finalized - Whether to get a finalized model (default: false)
   * @param type - The model type to export (default: 'stl')
   * @param filename - The filename to use for the export (default: 'model.stl')
   * @returns Promise resolving to a Blob containing the mesh data
   */
  private getMeshFromConfigurator(finalized: boolean = false, type: string = 'stl', filename: string = 'model.stl'): Promise<Blob> {
    devlog("getMeshFromConfigurator: Starting mesh request", { finalized, type, filename });
    
    // Create a promise to handle the mesh data retrieval
    return new Promise<Blob>((resolve, reject) => {
      // Dispatch event to request mesh from configurator
      this.addToPostInitQueue(() => {
        window.dispatchEvent(new CustomEvent('get-configurator-mesh', {
          detail: {
            finalized,
            type,
            filename,
            resolve: (blob: Blob) => {
              devlog("getMeshFromConfigurator: Received mesh blob", blob);
              
              if (!blob) {
                const error = new Error("Invalid mesh blob");
                error.name = "MeshRequestError";
                Object.assign(error, {
                  context: "Mesh blob is null or undefined",
                  component: "as-app",
                  appId: this.appId
                });
                reject(error);
                return;
              }
              
              resolve(blob);
            },
            reject: (err: Error) => {
              console.error("getMeshFromConfigurator: Error getting mesh:", err);
              const error = err || new Error("Failed to get mesh");
              error.name = error.name || "MeshFetchError";
              Object.assign(error, {
                context: "Error returned by get-configurator-mesh handler",
                component: "as-app",
                appId: this.appId
              });
              reject(error);
            }
          }
        }));
      });
    });
  }

  /**
   * Request mesh data from the configurator with modal display
   * 
   * Shows a modal during the process for transfer operations, then uses getMeshFromConfigurator
   * to retrieve the actual mesh data
   * 
   * @param finalized - Whether to get a finalized model (default: false)
   * @param type - The model type to export (default: 'stl')
   * @param filename - The filename to use for the export (default: 'model.stl')
   * @returns Promise resolving to a Blob containing the mesh data
   */
  private async meshFromConfigurator(finalized: boolean = false, type: string = 'stl', filename: string = 'model.stl'): Promise<Blob> {
    devlog("meshFromConfigurator: Starting mesh request with modal", { finalized, type, filename });
    
    // Update modal to show progress
    this.modalContent = { 
      type: 'saving',
      data: {
        status: 'Preparing mesh for download...'
      }
    };
    this.showModal();
    
    try {
      // Use the core mesh retrieval function
      const blob = await this.getMeshFromConfigurator(finalized, type, filename);
      return blob;
    } catch (error) {
      // Re-throw the error so the caller can handle it
      throw error;
    }
  }

  /**
   * Handle parameter change events
   * 
   * Processes parameter change events from child components and
   * updates the configurator with new values
   * 
   * @param event - Custom event containing parameter changes
   */
  private handleParamChange(event: CustomEvent): void {
    devlog("Handling param change event", event);
    const { paramPath, shouldConfigure = true } = event.detail ?? {};
    
    //TODO: we don't use the app store currently.

    // if (shouldConfigure) {
    //   this.reqConfigurator().configure({
    //     [paramPath]: {value:event.detail?.value}
    //   })
    //     .catch(err => {
    //       const errorEvent = new CustomEvent('configurator-error', {
    //         detail: {
    //           message: `Error configuring parameter ${paramPath}: ${err.message ?? 'Unknown error'}`,
    //           error: err
    //         },
    //         bubbles: true,
    //         composed: true
    //       });
    //       this.dispatchEvent(errorEvent);
    //     });
    // }
  }

  /**
   * Show auto-load dialog for the specified mesh
   * 
   * Checks if confirmation is required based on component attributes
   * If confirmation is required, shows a dialog; otherwise loads directly
   * 
   * @param autoLoadMesh - The stored mesh data to potentially load
   */
  private showAutoLoadDialog(autoLoadMesh: StoredMeshData): void {
    devlog("Checking for auto-load dialog requirements for mesh:", autoLoadMesh.meshName);
    
    // Check if the confirm-autoload attribute is present
    const confirmAutoloadRequired = this.hasAttribute('confirm-autoload');
    
    if (confirmAutoloadRequired) {
      devlog("confirm-autoload attribute is present, showing dialog");
      // Show the modal dialog as before
      this.modalContent = {
        type: 'autoload',
        data: {
          autoLoadMesh
        }
      };
      this.showModal();
    } else {
      devlog("confirm-autoload attribute is not present, automatically loading mesh");
      // Store mesh temporarily in modalContent for handleAutoLoadConfirm to use
      this.modalContent = {
        type: 'autoload',
        data: {
          autoLoadMesh
        }
      };
      // Call confirm handler directly without showing the dialog
      this.handleAutoLoadConfirm();
    }
  }

  /**
   * Handle configurator initialized event
   * 
   * Called when the configurator component signals it's fully initialized
   * Performs any post-initialization logic required by the application
   */
  private handleConfiguratorInitialized = async (): Promise<void> => {
    this.isInitialized = true;
    this.execPostInitQueue();
  }
  private addToPostInitQueue(operation:()=>void){
    if(this.isInitialized){ 
      operation();
    }else{
      this.postInitQueue.push(operation);
    }
  }

  private execPostInitQueue = async (): Promise<void> => {
    // devlog("handlePostInitQueue: Starting post-initialization queue");
    // Process all queued operations
    while (this.postInitQueue.length > 0) {
      const operation = this.postInitQueue.shift();
      if (operation) {
        await operation();
      }
    }
    // devlog("handlePostInitQueue: Post-initialization queue completed");
  }
  
  /**
   * Handle show-stored-meshes event
   * 
   * Fetches and displays history of stored meshes for the current app
   * Creates a modal dialog with mesh history when meshes are found
   * 
   * @param event - Custom event requesting mesh history display
   */
  private handleShowStoredMeshes = async (event: CustomEvent): Promise<void> => {
    devlog("Received show-stored-meshes event, loading mesh history");
    
    // Get all meshes for this app
    const meshData = await checkForStoredMeshData(this.appId, false);
    
    if (meshData.totalCount > 0) {
      devlog(`Found ${meshData.totalCount} mesh(es) for app ${this.appId}`);
      
      // Show history modal with meshes
      this.modalContent = {
        type: 'history',
        data: {
          meshes: meshData.meshes
        }
      };
      this.showModal();
    } else {
      devlog("No mesh history found for this app");
    }
  }

  /**
   * Show modal dialog with appropriate content
   * 
   * Uses the existing as-app-overlay modal element with content based on the current modalContent state
   * Handles different modal types (saving, success, error, history, autoload)
   */
  private showModal(): void {
    // If modal type is none, don't show anything
    if (this.modalContent.type === 'none') return;

    devlog("Showing modal with type:", this.modalContent.type);
    
    // Get the existing overlay from the shadow DOM
    const overlay = this.modalOverlay
    // const overlay = this.shadowRoot?.getElementById('as-app-overlay') as AsModalOverlay;
    if (!overlay) {
      console.error("Could not find as-app-overlay in shadow DOM");
      return;
    }
    
    // Create a unique panel ID for this modal content
    const panelId = `modal-panel-${this.modalContent.type}`;
    
    // Update the modal content based on type
    let modalContent = '';
    switch (this.modalContent.type) {
      case 'saving':
        modalContent = `<div id="${panelId}" class="visible">${this.getSavingModalContent()}</div>`;
        break;
      case 'success':
        devlog("Setting success content with blob:", !!this.modalContent.data?.blob);
        modalContent = `<div id="${panelId}" class="visible">${this.getTransferToAppModal()}</div>`;
        break;
      case 'error':
        modalContent = `<div id="${panelId}" class="visible">${this.getErrorModalContent()}</div>`;
        break;
      case 'history':
        modalContent = `<div id="${panelId}" class="visible">${this.getHistoryModalContent()}</div>`;
        break;
      case 'autoload':
        modalContent = `<div id="${panelId}" class="visible">${this.getAutoLoadModalContent()}</div>`;
        break;
    }
    
    // Store reference to the overlay
    // this.modalOverlay = overlay;
    
    // Add event listener for modal close events
    overlay.addEventListener('as-modal-closed', this.handleModalClosed);
    
    // Set the overlay content, preserving the error panel
    const errorPanel = overlay.querySelector('#main-error-panel');
    overlay.innerHTML = modalContent;
    
    // Ensure the error panel is preserved
    if (errorPanel) {
      overlay.appendChild(errorPanel);
    } else {
      const newErrorPanel = document.createElement('as-error-info');
      newErrorPanel.id = 'main-error-panel';
      overlay.appendChild(newErrorPanel);
    }
    
    // Hide the error panel when showing custom content
    const mainErrorPanel = overlay.querySelector('#main-error-panel');
    if (mainErrorPanel) {
      mainErrorPanel.classList.remove('visible');
    }
    
    // Set the overlay's visible-child attribute to our panel ID
    overlay.setAttribute('visible-child', panelId);
    
    // Open the modal
    overlay.open();
  }

  private openModal(modalId: string, tagName: string, attributes?: Record<string, string>): void {
    const overlay = this.modalOverlay;
    if (!overlay) return;

    let modalElement = overlay.querySelector(`#${modalId}`) as HTMLElement | null;
    if (!modalElement) {
      modalElement = document.createElement(tagName);
      modalElement.id = modalId;
      overlay.appendChild(modalElement);
    }

    // Update attributes whether element is new or existing
    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        modalElement!.setAttribute(key, value);
      });
    }

    overlay.setAttribute('visible-child', modalId);
    overlay.open(modalId);
  }

  private openHomeWarningModal(): void {
    this.openModal('home-warning', 'as-modal-home-warning');
  }

  private closeHomeWarningModal(): void {
    this.isHomeRedirectPending = false;
    if (this.modalOverlay?.hasAttribute('show')) {
      this.modalOverlay.close();
    }
  }

  private openClickthruModal(): void {
    const url = this.getAttribute('clickthru-url') || '';
    this.openModal('clickthru-modal', 'as-modal-clickthru-demo', { url });
  }

  private closeClickthruModal(): void {
    if (this.modalOverlay?.hasAttribute('show')) {
      this.modalOverlay.close();
    }
  }

  private redirectToStorefront(): void {
    this.isHomeRedirectPending = false;
    window.location.href = fullAppSuiteRootURL;
  }

  /**
   * Handle modal close event
   * 
   * Cleans up modal state and removes event listeners
   */
  private handleModalClosed = (): void => {
    // devlog("Modal closed event received");
    
    // Reset modal content state
    this.modalContent = { type: 'none' };
    this.isHomeRedirectPending = false;
    
    // Clean up modal content and reset state
    if (this.modalOverlay) {
      this.modalOverlay.removeEventListener('as-modal-closed', this.handleModalClosed);
      
      // Remove modal panels (event delegation handles clicks, so no need to remove listeners)
      const modalPanels = this.modalOverlay.querySelectorAll('[id^="modal-panel-"]');
      modalPanels.forEach(panel => {
        panel.remove();
      });
      
      // Reset the visible-child attribute to show the error panel
      const errorPanel = this.modalOverlay.querySelector('#main-error-panel');
      if (errorPanel) {
        errorPanel.classList.add('visible');
        this.modalOverlay.setAttribute('visible-child', 'main-error-panel');
      }
      
      // Clear the reference but keep the error panel
      // this.modalOverlay = null;
    }
  }

  /**
   * Handle clicks within the modal using event delegation
   * 
   * Processes interactions with modal UI elements:
   * - App selection for mesh transfer
   * - Autoload confirmation/cancellation
   * - Mesh history management
   * 
   * @param event - Mouse event from modal interaction
   */
  private handleTxModalClick = async (event: MouseEvent): Promise<void> => {
    const target = event.target as HTMLElement;
    
    // Only handle clicks on modal panels, not the overlay itself
    if (!target.closest('[id^="modal-panel-"]')) {
      return;
    }

    const newTabInput = target.closest('#transfer-input-open-in-new-tab') as HTMLInputElement;
    if(newTabInput){
      const openInNewTab = newTabInput.checked;
      devlog("Transfer input open in new tab", openInNewTab);
      this.openTransferInNewTab = openInNewTab;
    }
    // Handle app selection buttons
    const appButton = target.closest('.app-selection-button');
    if (appButton) {
      // Prevent default behavior to avoid unwanted navigation
      event.preventDefault();
      
      const selectedAppId = appButton.getAttribute('data-app');
      devlog("Selected app:", selectedAppId);
      
      if (selectedAppId && this.modalContent.type === 'success') {
        // Check if we have pendingStoreData (new flow) or existing mesh (old flow)
        const pendingData = this.modalContent.data?.pendingStoreData;
        
        if (pendingData) {
          // New flow: Save the pending mesh data with selected destination
          devlog("Saving mesh to database with selected destination:", selectedAppId);
          
          // Update the destination app
          pendingData.destinationApp = selectedAppId;
          
          // Show saving progress
          this.modalContent = {
            type: 'saving',
            data: {
              status: 'Saving mesh to database...'
            }
          };
          this.showModal();
          
          // Save to database with autoLoad=true
          storeMesh(pendingData, true)
            .then(async (savedMeshData) => {
              devlog("Mesh saved successfully:", savedMeshData.meshId, "autoLoad:", savedMeshData.autoLoad);
              
              // Update the meshData with the latest saved mesh
              if (!this.meshData) {
                this.meshData = { 
                  latestMesh: null, 
                  autoLoadMesh: null, 
                  totalCount: 0 
                };
              }
              this.meshData.latestMesh = savedMeshData;
              
              // Close modal and redirect to target app
              this.modalOverlay?.close();
              
              // Handle redirect to the target app
              const someAppInfo = appInfo[selectedAppId];
              devlog(someAppInfo, appInfo)
              await this.sendLog('transfer_mesh_started',{toApp:selectedAppId});
              const newAppRedirURL = `${fullAppSuiteRootURL}/apps/${selectedAppId}`
              if(!isProduction)devlog("redir to: ",newAppRedirURL)

              if (someAppInfo?.id) {
                devlog(this.openTransferInNewTab)
                if(this.openTransferInNewTab){
                  devlog("Opening in new tab");
                  window.open(newAppRedirURL, '_blank');
                } else {
                  window.location.href =  newAppRedirURL;
                }
              } else {
                console.warn(`No app link defined for ${selectedAppId}`);
              }
            })
            .catch(err => {
              console.error("Error saving mesh to database:", err);
              
              // Show error in error panel
              const errorPanel = this.shadowRoot?.getElementById('main-error-panel');
              errorPanel?.dispatchEvent(new CustomEvent('new-error', {
                detail: {
                  error: err,
                  info: "Failed to save mesh to database",
                  origin: 'mesh-storage'
                }
              }));
              const errorPanelOverlay = this.shadowRoot?.getElementById('as-app-overlay');
              errorPanelOverlay?.dispatchEvent(new CustomEvent('as-set-visible-child', {
                detail: { id: 'main-error-panel' }
              }));
              errorPanelOverlay?.dispatchEvent(new CustomEvent('open-modal'));
            });
        } else if (this.meshData.latestMesh) {
          // Old flow: Update existing mesh destination
          setNewApp(this.meshData.latestMesh.meshId, selectedAppId, true)
            .then(() => {
              devlog("Updated mesh destination app to:", selectedAppId, "with autoLoad=true");
              
              // Handle redirect to the target app
              const someAppInfo = appInfo[selectedAppId];
              if (someAppInfo?.id) {
                if(this.openTransferInNewTab){
                  devlog("Opening in new tab");
                  window.open(`${fullAppSuiteRootURL}/apps/${selectedAppId}`, '_blank');
                } else {
                  window.location.href = someAppInfo.url;
                }
              } else {
                console.warn(`No app link defined for ${selectedAppId}`);
              }
            })
            .catch(err => {
              console.error("Error updating mesh destination:", err);
              // Show error in error panel
              const errorPanel = this.shadowRoot?.getElementById('main-error-panel');
              errorPanel?.dispatchEvent(new CustomEvent('new-error', {
                detail: {
                  error: err,
                  info: "Failed to update mesh destination",
                  origin: 'mesh-transfer'
                }
              }));
              const errorPanelOverlay = this.shadowRoot?.getElementById('as-app-overlay');
              errorPanelOverlay?.dispatchEvent(new CustomEvent('as-set-visible-child', {
                detail: { id: 'main-error-panel' }
              }));
              errorPanelOverlay?.dispatchEvent(new CustomEvent('open-modal'));
            });
        }
      }
      
      // Note: Don't close modal here for new flow - it will be closed after successful save
      return;
    }
    
    // Handle autoload dialog actions
    if (this.modalContent.type === 'autoload') {
      if (target.closest('#confirm-autoload')) {
        devlog("User confirmed auto-load");
        this.handleAutoLoadConfirm();
        return;
      }
      
      if (target.closest('#cancel-autoload')) {
        devlog("User canceled auto-load");
        this.handleAutoLoadCancel();
        return;
      }
    }
    
    // Handle clear button in history modal
    const clearButton = target.closest('#clear-mesh-history');
    if (clearButton && this.modalContent.type === 'history') {
      devlog("Clear mesh history button clicked");
      deleteAllStoredTransfers()
        .then(() => {
          devlog("All mesh history cleared");
          // Close the modal
          this.modalOverlay?.close();
          
          // Update mesh data reference to empty state
          this.meshData = { 
            latestMesh: null, 
            autoLoadMesh: null,
            totalCount: 0 
          };
          
          devlog("Cleared mesh history, refreshing page to ensure clean state");
          // Force a page refresh to ensure clean state
          window.location.reload();
        })
        .catch(err => {
          console.error("Error clearing mesh history:", err);
          // Show error in error panel
          const errorPanel = this.shadowRoot?.getElementById('main-error-panel');
          errorPanel?.dispatchEvent(new CustomEvent('new-error', {
            detail: {
              error: err,
              info: "Failed to clear mesh history",
              origin: 'mesh-history'
            }
          }));
          const errorPanelOverlay = this.shadowRoot?.getElementById('as-app-overlay');
          errorPanelOverlay?.dispatchEvent(new CustomEvent('as-set-visible-child', {
            detail: { id: 'main-error-panel' }
          }));
          errorPanelOverlay?.dispatchEvent(new CustomEvent('open-modal'));
        });
    }
  }

  /**
   * Generate HTML content for saving state modal
   * 
   * @returns HTML string for the saving modal
   */
  private getSavingModalContent(): string {
    const status = this.modalContent.data?.status ?? 'Saving mesh...';
    return /*html*/`
      <div id="saving-content" class="visible" style="display: flex; flex-direction: column; gap: 1rem;">
        <div id="save-status">${status}</div>
        <div style="display: flex; justify-content: center;">
          <as-spinner style="--spinner-width: 3rem;"></as-spinner>
        </div>
      </div>
    `;
  }

  /**
   * Generate HTML content for success state modal
   * 
   * Creates UI for transferring mesh to other applications
   * 
   * @returns HTML string for the success modal
   */
  private getTransferToAppModal(): string {
    const availableApps = this.otherAppsForTransfer;
    const blob = this.modalContent.data?.blob;
    
    devlog("Getting success modal content, blob exists:", !!blob, "availableApps:", availableApps);
    
    if (!blob) return this.getErrorModalContent();
    
    return /*html*/`
      <div id="success-content" class="visible" style="display: flex; flex-direction: column; gap: 1rem;">
        <as-text id="save-status"dict-key="modals.transfer.ready.title">Mesh ready for transfer</as-text>
        <as-text dict-key="modals.transfer.ready.description">Your data may be lost! Please save your work before continuing.</as-text>
        <div style="border: 1px solid #ddd; border-radius: 4px; padding: 1rem;">
          <h3 style="margin: 0 0 0.5rem 0;"><as-text dict-key="modals.transfer.ready.action-label">Transfer to:</as-text></h3>
          <div style="display: flex; flex-direction: column; gap: 0.5rem;">
            ${availableApps.map(appId => {
              const someAppInfo = appInfo[appId];
              const appName = someAppInfo?.name ?? someAppInfo?.titleDefaultText ?? appId;
              
              // Always create as div to avoid default navigation behavior
              return `<div class="app-selection-button" data-app="${appId}" style="padding: 0.5rem; background: #f5f5f5; border-radius: 4px; cursor: pointer; text-decoration: none; color: inherit;">${appName}</div>`;
            }).join('')}
          </div>
          <div class="h-flex" style="margin-top: 1rem;">
            <input type="checkbox" id="transfer-input-open-in-new-tab" name="transfer-input-open-in-new-tab" value="true">
            <label for="transfer-input-open-in-new-tab"><as-text dict-key="modals.transfer.ready.open-in-new-tab">Open in new tab</as-text></label>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate HTML content for error state modal
   * 
   * @returns HTML string for the error modal
   */
  private getErrorModalContent(): string {
    const error = this.modalContent.data?.error ?? 'Unknown error';
    return /*html*/`
      <div id="error-content" class="visible" style="display: flex; flex-direction: column; gap: 1rem;">
        <div id="save-status" style="color: red;">Error: ${error}</div>
      </div>
    `;
  }

  /**
   * Handle mesh transfer process
   * 
   * Coordinates the entire mesh transfer workflow:
   * 1. Validates that required components exist
   * 2. Shows saving modal
   * 3. Creates mesh storage data structure 
   * 4. Retrieves mesh data from configurator
   * 5. Shows success modal with app selection
   * 6. Saves mesh to database only after user selects destination app
   * 
   * @param event - Custom event triggering the transfer
   */
  private prepareTransferMesh = async (_: CustomEvent): Promise<void> => {
    devlog("Starting transfer mesh process");
    
    // Check for as-configurator component
    const configuratorEl = this.querySelector('as-configurator');
    if (!configuratorEl) {
      throw new Error("Configurator component not found - The as-configurator component is required for mesh transfer but was not found in the DOM");
    }
    
    // Check if configurator is initialized
    const configInstance = configuratorEl as any;
    if (!configInstance.initialized) {
      // Notify user and return early
      this.dispatchEvent(new CustomEvent('as-notify', {
        detail: { 
          defaultText: 'Cannot transfer mesh: The configurator is not yet initialized. Please wait a moment and try again.', 
          dictKey: 'notifications.mesh.transfer.save.error',
          type: 'error'
        }
      }));
      return;
    }
    
    // Set modal to saving state and show it
    this.modalContent = { 
      type: 'saving',
      data: {
        status: 'Preparing mesh for transfer...'
      }
    };
    this.showModal();
    
    // Ensure available apps are loaded
    if (this.availableApps.length === 0) {
      await this.initializeAvailableApps();
    }

    devlog("Available apps", this.availableApps);
    let availableApps = this.otherAppsForTransfer;

    // Check if we have any apps available for transfer
    if (availableApps.length === 0) {
      console.warn('[AS-APP] No apps available for transfer on first attempt. Retrying appInfo load. appInfoCached:', appInfoCached);

      // Retry loading appInfo once
      try {
        await getAppInfo();
        await this.initializeAvailableApps();
        availableApps = this.otherAppsForTransfer;
      } catch (error) {
        console.error('[AS-APP] Failed to load appInfo on retry:', error);
      }

      // Check again after retry
      if (availableApps.length === 0) {
        console.error('[AS-APP] No apps available for transfer after retry. appInfoCached:', appInfoCached);
        this.dispatchEvent(new CustomEvent('as-notify', {
          detail: {
            defaultText: 'Cannot transfer mesh: No apps available for transfer. App configuration may not be loaded yet.',
            dictKey: 'notifications.mesh.transfer.no-apps.error',
            type: 'error'
          }
        }));

        // Close the saving modal
        if (this.modalOverlay) {
          this.modalOverlay.close();
        }
        return;
      }
    }

    // Create a new TransferSaveData object (but don't save to DB yet)
    let pendingStoreData: TransferSaveData;
    
    if (this.meshData.latestMesh) {
      // Use existing mesh data if available
      pendingStoreData = {
        authorId: this.meshData.latestMesh.authorId,
        meshName: this.meshData.latestMesh.meshName,
        meshId: this.meshData.latestMesh.meshId,
        currentApp: this.appId, // The app where transfer process starts
        destinationApp: 'none', // Will be set when user selects destination
        stlData: null
      };
    } else {
      // Create new mesh data with generated ID
      const timestamp = Date.now();
      const meshId = `mesh-${this.appId}-${timestamp}`;
      
      pendingStoreData = {
        authorId: this.userData?.eMail || 'unknown', // Default author ID
        meshName: `Mesh from ${this.appId}`, // Default mesh name
        meshId,
        currentApp: this.appId, // The app where transfer process starts
        destinationApp: 'none', // Will be set when user selects destination
        stlData: null
      };
      
      devlog("No existing mesh data found, created new mesh with ID:", meshId);
    }

    devlog("Getting mesh from configurator...");
    // Try to get mesh from configurator, with finalized=true
    this.meshFromConfigurator(true, 'stl', `${pendingStoreData.meshName}.stl`)
      .then(blob => {
        devlog("Got mesh blob, size:", blob.size);
        // Set store data but don't save to DB yet
        pendingStoreData.stlData = blob;
        
        // Store the pending data in modalContent for later use
        this.modalContent = { 
          type: 'success',
          data: {
            blob: blob,
            availableApps,
            pendingStoreData // Store the data temporarily
          }
        };
        this.showModal();
        

      })
      .catch(err => {
        console.error("Error in mesh transfer process:", err);
        
        // Show error in error-info panel instead of error modal
        const errorPanel = this.shadowRoot?.getElementById('main-error-panel');
        errorPanel?.dispatchEvent(new CustomEvent('new-error', {
          detail: {
            error: err,
            info: "Error occurred during mesh transfer operation (meshFromConfigurator)",
            origin: 'mesh-transfer'
          }
        }));
        const errorPanelOverlay = this.shadowRoot?.getElementById('as-app-overlay');
        errorPanelOverlay?.dispatchEvent(new CustomEvent('open-modal'));
        
        // Notify about the error
        this.dispatchEvent(new CustomEvent('as-notify', {
          detail: { 
            defaultText: 'Error during mesh transfer: ' + err.message, 
            dictKey: 'notifications.mesh.transfer.save.error',
            type: 'error'
          }
        }));
        
        // Close the saving modal
        if (this.modalOverlay) {
          this.modalOverlay.close();
        }
      });
  }


  clearTransferDB(){
    return deleteAllStoredTransfers()
  }

  /**
   * Generate HTML content for mesh history modal
   * 
   * Creates a table display of previously saved meshes with:
   * - Mesh name
   * - Last save timestamp
   * - Origin app
   * - Option to clear all history
   * 
   * @returns HTML string for the history modal
   */
  private getHistoryModalContent(): string {
    const meshes = this.modalContent.data?.meshes ?? [];
    
    // Format date to a more readable format
    const formatDate = (date: Date): string => {
      return new Date(date).toLocaleString();
    };
    
    return /*html*/`
      <div id="history-content" class="visible" style="display: flex; flex-direction: column; gap: 1rem; max-width: 500px;">
        <h2 style="margin: 0 0 1rem 0;">Mesh History</h2>
        <div style="max-height: 300px; overflow-y: auto; border: 1px solid #ddd; border-radius: 4px;">
          ${meshes.length === 0 
            ? '<div style="padding: 1rem; text-align: center;">No mesh history found</div>' 
            : `<table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr>
                    <th style="padding: 0.5rem; text-align: left; border-bottom: 1px solid #ddd;">Name</th>
                    <th style="padding: 0.5rem; text-align: left; border-bottom: 1px solid #ddd;">Last Save</th>
                    <th style="padding: 0.5rem; text-align: left; border-bottom: 1px solid #ddd;">Origin</th>
                  </tr>
                </thead>
                <tbody>
                  ${meshes.map(mesh => `
                    <tr>
                      <td style="padding: 0.5rem; border-bottom: 1px solid #eee;">${mesh.meshName}</td>
                      <td style="padding: 0.5rem; border-bottom: 1px solid #eee;">${formatDate(mesh.lastSave)}</td>
                      <td style="padding: 0.5rem; border-bottom: 1px solid #eee;">${mesh.lastApp}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>`
          }
        </div>
        <div style="display: flex; justify-content: space-between; margin-top: 1rem;">
          <button id="clear-mesh-history" style="padding: 0.5rem 1rem; background-color: #f44336; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Clear All History
          </button>
          <button class="close-modal" style="padding: 0.5rem 1rem; background-color: #ddd; border: none; border-radius: 4px; cursor: pointer;">
            Close
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Generate HTML content for autoLoad modal
   * 
   * Creates a confirmation dialog for restoring previously saved mesh with:
   * - Mesh details (name, origin app, save time)
   * - Options to load or skip
   * 
   * @returns HTML string for the autoload modal
   */
  private getAutoLoadModalContent(): string {
    const mesh = this.modalContent.data?.autoLoadMesh;
    
    if (!mesh) {
      return this.getErrorModalContent();
    }
    
    // Format date to a more readable format
    const formatDate = (date: Date): string => {
      return new Date(date).toLocaleString();
    };
    
    return /*html*/`
      <div id="autoload-content" class="visible" style="display: flex; flex-direction: column; gap: 1rem; max-width: 500px;">
        <h2 style="margin: 0 0 1rem 0;">Restore Mesh</h2>
        <div style="padding: 1rem; border: 1px solid #ddd; border-radius: 4px;">
          <p>A previously saved mesh was found that can be loaded:</p>
          <div style="margin: 1rem 0;">
            <div><strong>Name:</strong> ${mesh.meshName}</div>
            <div><strong>From app:</strong> ${mesh.lastApp}</div>
            <div><strong>Last saved:</strong> ${formatDate(mesh.lastSave)}</div>
          </div>
          <p>Would you like to load this mesh?</p>
        </div>
        <div style="display: flex; justify-content: space-between; margin-top: 1rem;">
          <button id="confirm-autoload" style="padding: 0.5rem 1rem; background-color: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Load Mesh
          </button>
          <button id="cancel-autoload" style="padding: 0.5rem 1rem; background-color: #ddd; border: none; border-radius: 4px; cursor: pointer;">
            Skip
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Handle auto-load confirmation
   * 
   * Processes mesh auto-loading when user confirms:
   * 1. Dispatches event with mesh data for loading
   * 2. Marks mesh for deletion after processing
   */
  private handleAutoLoadConfirm(): void {
    const mesh = this.modalContent.data?.autoLoadMesh;
    
    if (!mesh) {
      console.error("No auto-load mesh found in modal data");
      this.modalOverlay?.close();
      return;
    }
    
    // Close the modal
    this.modalOverlay?.close();
    
    // If mesh has stlData, create event to notify app
    if (mesh.stlData) {
      devlog("Dispatching as-db-mesh-loaded event with STL data, size:", mesh.stlData.size);
      
      // Notify that mesh was detected
      this.dispatchEvent(new CustomEvent('as-notify', {
        detail:   { 
          defaultText: `Detected mesh: ${mesh.meshName}`, 
          dictKey: 'notifications.mesh.autoload.detected',
          type: 'info'
        }
      }));
      
      // Mark mesh for deletion after processing
      const meshWithDelete = {
        ...mesh,
        queueDelete: true
      };
      let stlFile: File;

      // Create a new File from the Blob with a name
      const fileName = `${mesh.meshName}.stl`;
      stlFile = new File([mesh.stlData], fileName, {
        type: 'application/octet-stream'
      });
      // Dispatch as-db-mesh-loaded event with the mesh data
      this.addToPostInitQueue(()=>{
        this.dispatchEvent(new CustomEvent('as-db-mesh-loaded', {
          detail: {
            mesh: meshWithDelete,
            meshId: mesh.meshId,
            stl: stlFile
          },
          bubbles: true,
          composed: true
        }));
      });
    } else {
      console.warn("Mesh has no STL data to load");
      
      // If no data to load, we can immediately set autoLoad to false
      setNewApp(mesh.meshId, this.appId, false)
        .then(() => {
          devlog("Reset autoLoad flag for mesh without STL data");
        })
        .catch(err => {
          console.error("Error resetting autoLoad flag:", err);
        });
    }
  }

  /**
   * Handle auto-load cancellation
   * 
   * Resets autoLoad flag on mesh when user cancels loading
   */
  private handleAutoLoadCancel(): void {
    const mesh = this.modalContent.data?.autoLoadMesh;
    
    if (mesh) {
      // Reset the autoLoad flag on the mesh
      setNewApp(mesh.meshId, this.appId, false)
        .then(() => {
          devlog("Reset autoLoad flag after user canceled auto-load");
        })
        .catch(err => {
          console.error("Error resetting autoLoad flag:", err);
        });
    }
    
    // Close the modal
    this.modalOverlay?.close();
  }

  /**
   * Post-processing cleanup after mesh receive
   * 
   * Handles database operations after mesh has been processed:
   * - Either deletes the mesh if requested
   * - Or resets the autoLoad flag to prevent future auto-loading
   * 
   * @param event - Custom event containing mesh processing result
   */
  private postMeshReceiveCleanup = async (event: CustomEvent): Promise<void> => {
    const { meshId, shouldDelete } = event.detail;
    // devlog("Handling mesh cleanup for mesh: ", meshId, "shouldDelete: ", shouldDelete);
    if (!meshId) {
      console.error("No meshId provided in mesh-processed event");
      return;
    }
    
    // devlog(`Handling mesh processed event for mesh: ${meshId}, shouldDelete: ${shouldDelete}`);
    const senderApp = await getSenderApp(meshId);
    if(senderApp===null){
      console.error("No sender app found for mesh: ", meshId);
      return;
    }
    
    sendLogToServer('transferred_mesh_received',{fromApp:senderApp})
    try {
      if (shouldDelete!==false) {
        // If should delete is explicitly set to true, delete the mesh
        devlog(`Deleting mesh ${meshId} as requested`);
        await deleteMesh(meshId);
      } else {
        // Otherwise, just set autoLoad to false to prevent it from loading again
        devlog(`Setting autoLoad to false for mesh ${meshId}`);
        await setNewApp(meshId, this.appId, false);
      }
    } catch (err) {
      console.error("Error processing mesh:", err);
      
      // Show error in error panel
      const errorPanel = this.shadowRoot?.getElementById('main-error-panel');
      errorPanel?.dispatchEvent(new CustomEvent('new-error', {
        detail: {
          error: err,
          info: "Failed to process mesh",
          origin: 'mesh-processing'
        }
      }));
      const errorPanelOverlay = this.shadowRoot?.getElementById('as-app-overlay');
      errorPanelOverlay?.dispatchEvent(new CustomEvent('open-modal'));
    }
  }

  /**
   * Send localized text to a subscriber
   * 
   * Finds the appropriate text for the current language and sends it to the subscriber
   * 
   * @param textKey - The text key to look up
   * @param subscriber - The element that requested the text
   * @returns boolean indicating if text was found and sent
   */
  private sendTextToSubscriber(textKey: string, subscriber: HTMLElement): boolean {
    let textFound = false;
    // devlog("langText", this.langText);
    // if(subscriber.id==="test-text") devlog("=== AS APP: Sending text to subscriber", {textKey, subscriber});
    // First check in sharedLocalisation
    if (this.sharedLocalisation && this.language && 
        this.sharedLocalisation[this.language] && 
        this.sharedLocalisation[this.language][textKey]) {
      
      subscriber.dispatchEvent(new CustomEvent('as-local-text-update', {
        detail: { text: this.sharedLocalisation[this.language][textKey] }
      }));
      textFound = true;
    }
    // Then check in langText if available
    else if (this.langText && this.language && 
             this.langText[this.language] && 
             this.langText[this.language][textKey]) {
      
      subscriber.dispatchEvent(new CustomEvent('as-local-text-update', {
        detail: { text: this.langText[this.language][textKey] }
      }));
      textFound = true;
    }
    // Finally check defaultTranslations if available
    else if (defaultTranslations[this.language] && defaultTranslations[this.language][textKey]) {
      subscriber.dispatchEvent(new CustomEvent('as-local-text-update', {
        detail: { text: defaultTranslations[this.language][textKey] }
      }));
      textFound = true;
    }
    // If still not found and is a tooltip, use the key as fallback (without 'tooltip.' prefix)
    else if (textKey.startsWith('tooltip.')) {
      const simplifiedKey = textKey.substring(textKey.lastIndexOf('.') + 1);
      const tooltipText = simplifiedKey.charAt(0).toUpperCase() + simplifiedKey.slice(1);
      subscriber.dispatchEvent(new CustomEvent('as-local-text-update', {
        detail: { text: tooltipText }
      }));
      textFound = true;
    }
  
    
    return textFound;
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-app')) {
  customElements.define('as-app', AsApp);
} else {
  console.warn("as-app already defined");
}
